import React from "react";
import { MailOutlined } from "@ant-design/icons";
import { notification, Tabs, Descriptions, Collapse, List, Button } from "antd";
import PageLoader from "./PageLoader";
import { gql, useQuery } from "@apollo/client";
import { migrationNotesTab } from "./ProjectNotes";
import { Navigate, useParams } from "react-router-dom";
import prettyDateString from "../lib/prettyDateString";
import argoLink from "../lib/argoLink";
import properCase from "../lib/properCase";
import { sendMigrationRunClientEmail } from "./MigrationRunList";
import { CreateMigrationRunButton } from "./CreateMigrationRunButton";
import { statusIcon } from "../lib/status";
import { MigrationFiles } from "./migrationRunDetails/MigrationFiles";
import MigrationNotificationList from "./MigrationNotificationList";
import migrationSources from "../lib/migrationSources";
import { admBaseUri } from "../lib/env";
import { usePermissions } from "../lib/usePermissions";

const { TabPane } = Tabs;
const { Panel } = Collapse;

const MIGRATION_RUN_QUERY = gql`
  query MigrationRunQuery($migrationId: ID!) {
    migration(id: $migrationId) {
      id
      name
      createdAt
      updatedAt
      status
      scheduledStartTime
      argoWorkflowId
      clientEmailsSent
      sendClientEmails
      signOffDueByDate
      runType
      user {
        firstName
        lastName
      }
      project {
        id
        company
      }
      pipelines {
        id
        createdAt
        updatedAt
        name
        includeCustomFields
        includeLocations
        includeVendors
        includeItems
        includeInventoryOnHand
        includeCustomers
        includeStoreCredits
        includeGiftCards
        includePurchaseOrders
        includeSalesHistory
        includeSavedReports
        includeReasonCodes
        includeCustomBranding
        includeCompanyDetails
        includeRoles
        includeCustomerNotes
        includeCopyTenantToProduction
        includeGridTemplates
        includeUsers
        includeSettings
        includeCustomPaymentTypes
        includeProductPacks
        includeTemplates
        includeFeatureFlags
        salesHistoryFilterDate
        source {
          id
          createdAt
          updatedAt
          type
          name
          subdomain
          agentToken
          sourceSchemaName
        }
        destination {
          id
          createdAt
          updatedAt
          type
          name
          subdomain
          tenantType
          featureTier
          inventoryAdjustmentReason
          giftCardAdjustmentReason
          accountId
        }
      }
      events {
        id
        message
        createdAt
      }
      artifacts {
        id
        updatedAt
        name
        url
      }
      files {
        id
        filename
        url
        updatedAt
      }
    }
  }
`;

function migrationRunDetailsTab(migration: any): JSX.Element {
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Descriptions title="Migration Run Details">
          <Descriptions.Item label="Status">
            {migration.status}
          </Descriptions.Item>
          <Descriptions.Item label="Migration">
            <a href={`/migrations/${migration.project.id}`}>
              {migration.project.company}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Argo Workflow ID">
            {argoLink(migration.argoWorkflowId)}
          </Descriptions.Item>
          <Descriptions.Item label="Scheduled Start Time">
            {prettyDateString(migration.scheduledStartTime)}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {prettyDateString(migration.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {prettyDateString(migration.updatedAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Send Email On Complete">
            {migration.sendClientEmails.toString()}
          </Descriptions.Item>
          <Descriptions.Item label="Email Sent">
            {migration.clientEmailsSent.toString()}
          </Descriptions.Item>
          <Descriptions.Item label="Sign Off Due By">
            {migration.signOffDueByDate}
          </Descriptions.Item>
          <Descriptions.Item label="Migration Run Type">
            {migration.runType}
          </Descriptions.Item>
          <Descriptions.Item label="Migration Rep">
            {migrationUserName(migration.user)}
          </Descriptions.Item>
          {destinationTenant(migration) && (
            <Descriptions.Item label="ADM Tenant">
              {destinationTenant(migration)}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Source System">
            {sourceSystemNames(migration)}
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div style={{ float: "left" }}>
        <MigrationRunButtons migration={migration} />
      </div>
    </div>
  );
}

function sourceSystemNames(migration: any): string | undefined {
  let sources = [];
  let pipelines = migration.pipelines || [];
  pipelines.forEach((pipeline) => {
    if (pipeline.source) {
      let source = migrationSources[pipeline.source.type];
      if (source) {
        sources.push(source.name);
      }
    }
  });
  return sources.join(", ");
}

function destinationTenant(migration: any): string | undefined {
  let tenantLink;
  let pipelines = migration.pipelines || [];
  pipelines.forEach((pipeline) => {
    let destination = pipeline.destination || {};
    if (destination.subdomain && destination.type !== "sr-restore-backup") {
      const previewBaseDomain = admBaseUri;
      let url = `https://${destination.subdomain}.${previewBaseDomain}/`;
      tenantLink = (
        <a href={url} target="_blank" rel="noreferrer">
          {destination.subdomain}
        </a>
      );
    }
  });
  return tenantLink;
}

function migrationUserName(user: any): string {
  if (user) {
    return `${user.firstName} ${user.lastName}`;
  } else {
    return "Unknown";
  }
}

function migrationPipelinesTab(migration: any): JSX.Element {
  const pipelines = migration.pipelines || [];
  return <Collapse bordered={false}>{pipelinePanels(pipelines)}</Collapse>;
}

function artifactDisplayName(artifact: any): string {
  let name: string = artifact.name || "";
  name = name.replace(/-/g, " ").replace(" artifact", "");
  return name;
}

export function migrationArtifactsTab(migration: any): JSX.Element {
  const artifacts: any[] = migration.artifacts || [];
  artifacts.slice().reverse();
  return (
    <List
      itemLayout="vertical"
      dataSource={artifacts}
      renderItem={(artifact) => (
        <List.Item
          key={artifact.id}
          actions={[<a href={artifact.url}>Download</a>]}
        >
          <List.Item.Meta
            title={artifactDisplayName(artifact)}
            description={prettyDateString(artifact.updatedAt)}
          />
        </List.Item>
      )}
    />
  );
}

export function migrationMessagesTab(migration: any): JSX.Element {
  return <MigrationNotificationList migrationId={migration.id} />;
}

export function migrationEventsTab(migration: any): JSX.Element {
  const events: any[] = migration.events || [];
  events.slice().reverse();
  return (
    <List
      itemLayout="horizontal"
      dataSource={events}
      renderItem={(event) => (
        <List.Item>
          <List.Item.Meta
            title={event.message}
            description={prettyDateString(event.createdAt)}
          />
        </List.Item>
      )}
    />
  );
}

function pipelinePanels(pipelines: any[]): JSX.Element[] {
  const panels = [];
  let key = 0;
  pipelines.forEach((pipeline) => {
    key += 1;
    panels.push(
      <Panel
        header={`Pipeline # ${pipeline.id} - ${pipeline.name}`}
        key={key.toString()}
      >
        <Descriptions title="Pipeline Details">
          <Descriptions.Item label="ID">{pipeline.id}</Descriptions.Item>
          <Descriptions.Item label="Created At">
            {prettyDateString(pipeline.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {prettyDateString(pipeline.updatedAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Name">{pipeline.name}</Descriptions.Item>
          {pipeline.salesHistoryFilterDate && (
            <Descriptions.Item label="Sales History Filter Date">
              {pipeline.salesHistoryFilterDate}
            </Descriptions.Item>
          )}
        </Descriptions>
        {pipelineIncludedRecordTypes(pipeline)}
        {pipelineSourceDetails(pipeline)}
        {pipelineDestinationDetails(pipeline)}
      </Panel>,
    );
  });
  return panels;
}

function pipelineSourceDetails(pipeline: any): JSX.Element {
  const descriptions = [];

  for (let field in pipeline.source) {
    let value = pipeline.source[field];
    if (value && field != "__typename") {
      if (field == "createdAt" || field == "updatedAt") {
        value = prettyDateString(value);
      } else if (field == "agentToken") {
        field = "agent";
        value = <a href="/agents">{value}</a>;
      }
      descriptions.push(
        <Descriptions.Item key={field} label={properCase(field)}>
          {value}
        </Descriptions.Item>,
      );
    }
  }

  return <Descriptions title="Source Details">{descriptions}</Descriptions>;
}

function pipelineDestinationDetails(pipeline: any): JSX.Element {
  const descriptions = [];

  for (let field in pipeline.destination) {
    let value = pipeline.destination[field];
    if (value && field != "__typename") {
      if (field == "createdAt" || field == "updatedAt") {
        value = prettyDateString(value);
      }
      descriptions.push(
        <Descriptions.Item key={field} label={<b>{properCase(field)}</b>}>
          {value}
        </Descriptions.Item>,
      );
    }
  }

  return (
    <Descriptions title="Destination Details">{descriptions}</Descriptions>
  );
}

function pipelineIncludedRecordTypes(pipeline: any): JSX.Element {
  const descriptions = [];

  for (let field in pipeline) {
    if (field.includes("include")) {
      let fieldName = properCase(field.replace("include", ""));
      let value = pipeline[field];
      if (value) {
        descriptions.push(
          <Descriptions.Item key={field} label={fieldName}>
            {value.toString()}
          </Descriptions.Item>,
        );
      }
    }
  }

  return (
    <Descriptions title="Included Record Types">{descriptions}</Descriptions>
  );
}

function migrationDetailsTabs(migration: any): JSX.Element {
  return (
    <Tabs defaultActiveKey="1">
      <TabPane tab="Details" key="1">
        {migrationRunDetailsTab(migration)}
      </TabPane>
      <TabPane tab="Pipelines" key="2">
        {migrationPipelinesTab(migration)}
      </TabPane>
      <TabPane tab="Source Files" key="3">
        <MigrationFiles dataMigrationId={migration.id} />
      </TabPane>
      {/* TODO: Use proxy to download output files */}
      {/* <TabPane tab="Output Files" key="4">
        {migrationArtifactsTab(migration)}
      </TabPane> */}
      <TabPane tab="Notifications" key="5">
        {migrationMessagesTab(migration)}
      </TabPane>
      <TabPane tab="Events" key="6">
        {migrationEventsTab(migration)}
      </TabPane>
      <TabPane tab="Notes" key="7">
        {migrationNotesTab(migration)}
      </TabPane>
    </Tabs>
  );
}

function MigrationRunButtons({ migration }: { migration: any }): JSX.Element {
  const { hasPermission } = usePermissions();
  const buttons = [];
  if (migration.status == "success") {
    buttons.push(
      <Button
        key="sendEmails"
        icon={<MailOutlined />}
        data-migration-run-id={migration.id}
        onClick={sendMigrationRunClientEmail}
      >
        Send Client Email
      </Button>,
    );
  }
  if (
    migration.status != "running" &&
    hasPermission("migrations_runs.manage")
  ) {
    buttons.push(
      <CreateMigrationRunButton
        buttonText="Copy Migration Run"
        key="createMigration"
        style={{ float: "right" }}
        migrationSearchVal={`${migration.id} - ${migration.name}`}
      />,
    );
  }

  return <>{buttons}</>;
}

function MigrationRunDetails() {
  const { migration_run_id } = useParams();
  const migrationId = migration_run_id;
  const { loading, error, data } = useQuery(MIGRATION_RUN_QUERY, {
    variables: { migrationId },
    pollInterval: 5000,
  });

  if (loading) return <PageLoader tip="Loading..." delay={500} />;

  if (error || data.migration == null) {
    if (error) {
      console.error("Failed to retrieve migration", error);
      notification["error"]({
        message: `Error loading migration ${migrationId}. Redirecting you to Migration Run list.`,
        description: error.message,
      });
    } else {
      notification["info"]({
        message: `Migration "${migrationId}" not found. Redirecting you to Migration Run list`,
      });
    }
    return <Navigate to="/migration-runs" replace />;
  }

  return (
    <div>
      <h1>
        <span style={{ paddingRight: 15 }}>
          {statusIcon(data.migration.status, 22)}
        </span>
        <span>Migration Run # </span>
        {data.migration.id} - {data.migration.name}
      </h1>
      {migrationDetailsTabs(data.migration)}
    </div>
  );
}

export default MigrationRunDetails;
